import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Building2, Crown, ArrowLeft, RefreshCw, LayoutDashboard,
  DollarSign, CreditCard, Settings, ClipboardCheck, Briefcase, Plus,
  TrendingUp, Activity, Loader2, AlertCircle,
  Pencil, Trash2, UserCog, Power, X, Search, Mail, Shield, ShieldCheck, Check, ArrowUpDown, ArrowUp, ArrowDown, BarChart3, FlaskConical, ExternalLink, Bug, ChevronDown , Upload} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, { unitsAPI } from '../services/api';

interface PlatformAnalytics {
  total_condos: number;
  total_users: number;
  total_units: number;
  active_condos: number;
  inactive_condos: number;
  active_users: number;
  inactive_users: number;
  monthly_revenue: number;
  currency: string;
  top_condos_by_users: Array<{ condo_id: string; condo_name: string; user_count: number; }>;
  growth_30d: { new_users: number; new_condos: number; growth_percentage: number; };
}

interface Condo {
  id: string;
  name: string;
  country?: string;
  city?: string;
  address_data?: Record<string, string | undefined>;
  status?: string;
  plan?: string;
  primary_currency?: string;
  default_due_day?: number;
  unit_count?: number;
  registered_units?: number;
  created_at?: string;
  monthly_fee?: number;
  address_formatted?: string;
  admins?: CondoMember[];
  users_by_role?: Record<string, number>;
  // F-12 (2026-09-25): read-only hierarchy block from GET /condos (backend
  // condo/routes.js). linkStatus is derived display data, never a state.
  parentCondo?: { id: string | null; name: string | null; status: string | null; linkStatus?: 'linked' | 'standalone' };
  childCondos?: Array<{ id: string; name: string; status?: string | null }>;
}

interface CondoMember {
  user_id: string;
  name: string;
  email: string;
  role: string;
}

interface StandaloneUnit {
  id: string;
  unit_number: string;
  owner_name?: string | null;
  owner_email?: string | null;
  unit_type?: string | null;
  floor?: string | number | null;
  square_meters?: number | null;
  parking?: string | null;
  storage?: string | null;
  address_data?: { street?: string; number?: string; neighborhood?: string; locality?: string; city?: string; postal_code?: string } | null;
  city?: string | null;
  country?: string | null;
  status?: string | null;
  occupancy_status?: string | null;
  household_name?: string | null;
  householdName?: string | null;
  created_at?: string | null;
}

interface ManagedUnit {
  id: string;
  unit_number: string;
  status?: string;
  occupancy_status?: string;
  section_id?: string | null;
  section_name?: string | null;
  internal_address?: string | null;
  unit_type?: string | null;
  floor?: string | null;
  square_meters?: number | null;
  parking?: string | null;
  storage?: string | null;
  owner?: { id: string; name?: string; email?: string } | null;
  resident?: { id: string; name?: string; email?: string } | null;
}

function toast(msg: string, type: 'success' | 'error' = 'success') {
  const el = document.createElement('div');
  el.className = `fixed top-4 right-4 z-[60] ${type === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white px-4 py-2 rounded-lg shadow-lg`;
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  // 2026-09-24 (Jaime 15:50): collapsible "Detalle por fila" in QA matrix; persisted in localStorage
  const [matrixDetailOpen, setMatrixDetailOpen] = useState<boolean>(() => {
    try { return window.localStorage.getItem('admin-matrix-detail-open') === '1'; } catch { return false; }
  });
  const toggleMatrixDetail = useCallback(() => {
    setMatrixDetailOpen((v) => {
      const next = !v;
      try { window.localStorage.setItem('admin-matrix-detail-open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const [condos, setCondos] = useState<Condo[]>([]);
  // F-12: read-only hierarchy detail per condo row (parent + children).
  const [hierarchyCondoId, setHierarchyCondoId] = useState<string | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState<Record<string, { parent: { name: string; status?: string | null } | null; children: Array<{ id: string; name: string; status?: string | null }> }>>({});
  const [condosLoading, setCondosLoading] = useState(false);
  const [condosError, setCondosError] = useState<string | null>(null);

  // Edit modal (F2)
  const [editCondo, setEditCondo] = useState<Condo | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete modal (F3)
  const [deleteCondo, setDeleteCondo] = useState<Condo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Assign admin modal (F5)
  const [adminCondo, setAdminCondo] = useState<Condo | null>(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState<any[]>([]);
  const [adminSearching, setAdminSearching] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [currentAdmins, setCurrentAdmins] = useState<CondoMember[]>([]);
  const [currentAdminsLoading, setCurrentAdminsLoading] = useState(false);

  // Unit management (F116)
  const [unitsCondo, setUnitsCondo] = useState<Condo | null>(null);
  const [managedUnits, setManagedUnits] = useState<ManagedUnit[]>([]);
  const [unitsCap, setUnitsCap] = useState<number | null>(null);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitDeleting, setUnitDeleting] = useState<string | null>(null);
  const [unitSearch, setUnitSearch] = useState('');
  const [unitSort, setUnitSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'unit_number', direction: 'asc' });
  const [unitFormOpen, setUnitFormOpen] = useState(false);
  const [unitEditing, setUnitEditing] = useState<string | null>(null);
  const [unitSaving, setUnitSaving] = useState(false);
  const emptyUnitForm = { unit_number: '', section: '', internal_address: '', status: '', unit_type: '', floor: '', square_meters: '', parking: '', storage: '' };
  const [unitForm, setUnitForm] = useState(emptyUnitForm);
  const [unitOriginalRich, setUnitOriginalRich] = useState<Record<string, string>>({});

  // Standalone units (global, read-only SuperAdmin view)
  const [standaloneUnits, setStandaloneUnits] = useState<StandaloneUnit[]>([]);
  const [standaloneSearch, setStandaloneSearch] = useState('');
  const [standaloneLoading, setStandaloneLoading] = useState(false);
  const [standaloneError, setStandaloneError] = useState<string | null>(null);
  const [dashboardsLoading, setDashboardsLoading] = useState(false);
  const [dashboardsError, setDashboardsError] = useState<string | null>(null);
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  // 2026-09-24 (Jaime 13:00): condo list moved behind a tile — modal toggle.
  const [condosOpen, setCondosOpen] = useState(false);
  const [standaloneEditing, setStandaloneEditing] = useState<StandaloneUnit | null>(null);
  const [standaloneForm, setStandaloneForm] = useState({ unit_type: '', floor: '', square_meters: '', parking: '', storage: '', status: '', household_name: '' });
  const [standaloneOriginal, setStandaloneOriginal] = useState<Record<string, string>>({});
  const [standaloneSaving, setStandaloneSaving] = useState(false);

  const superAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : user?.role === 'super_admin';

  // 2026-09-24 (Jaime 13:00): super-admin bugs trend (GET /api/admin/bug-trend?days=N,
  // super_admin-only; same pattern as /quejas). Replaces the inline condo list card
  // in the freed dashboard space.
  const [bugTrends, setBugTrends] = useState<any>(null);
  // 2026-09-25: last query params kept for the 5-min auto-refresh (preserves
  // the active window / custom range across silent refreshes).
  const trendLastQueryRef = useRef<{ window?: string; from?: string; to?: string } | undefined>(undefined);
  // 2026-09-25: per-category trend rows from GET /api/admin/bug-trend
  type TrendCat = { bug: number; sugerencia: number; queja: number; total: number };
  type TrendDay = { date: string; encontradas?: TrendCat; resueltas?: TrendCat };
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendWindow, setTrendWindow] = useState<'7' | '30' | 'custom'>('30');
  const [trendFrom, setTrendFrom] = useState('');
  const [trendTo, setTrendTo] = useState('');

  // 2026-09-24 (Jaime 14:00): comprehensive /admin chart set. Reuses EXISTING
  // super_admin endpoints only — no new backend code, no invented data:
  //   - /analytics/trends (product analytics): daily active users + page views by module (7|30 days)
  //   - /condos: country, plan and users_by_role per condo (aggregated client-side)
  const [usageTrends, setUsageTrends] = useState<any>(null);
  const [usageWindow, setUsageWindow] = useState<'7' | '30'>('30');
  const [usageLoading, setUsageLoading] = useState(false);
  const [allCondos, setAllCondos] = useState<Condo[]>([]);
  const [allCondosLoading, setAllCondosLoading] = useState(false);

  // 2026-09-24 (Jaime 14:20 amendment): Matriz de pruebas (QA) — served via the
  // super-admin endpoint GET /api/admin/test-matrix (shape
  // {success, data: {matrix, history, generated_at}}), fetched with cache:'no-store'.
  // Same data as audit-dashboard.html. matrix null / endpoint down → section hides.
  const [testMatrix, setTestMatrix] = useState<any>(null);
  const [testMatrixHistory, setTestMatrixHistory] = useState<any>(null);
  const [testMatrixLoading, setTestMatrixLoading] = useState(false);

  // 2026-09-24 (Jaime 14:12): prefer the NEW super-admin endpoint
  // GET /api/admin/platform-snapshots?days=N (normalized to activeUsers[]);
  // falls back to /analytics/trends while the endpoint is unavailable.
  const loadUsageTrends = useCallback(async (days: '7' | '30') => {
    if (!superAdmin) return;
    setUsageLoading(true);
    try {
      try {
        const res = await api.get('/admin/platform-snapshots', { params: { days } });
        const env = res && res.data !== undefined ? res.data : res;
        const d = env && env.data !== undefined ? env.data : env;
        // 2026-09-24 (bugfix, live QA): endpoint returns {success, data:[{snapshot_date,
        // total_users, active_users, module_views:{top:[{module,views}]}}]}.
        const snaps: any[] | null = Array.isArray(d) ? d
          : Array.isArray(d?.data) ? d.data
          : Array.isArray(d?.snapshots) ? d.snapshots
          : Array.isArray(d?.items) ? d.items
          : Array.isArray(d?.days) ? d.days
          : null;
        if (snaps && snaps.length) {
          // date field is snapshot_date in the live API; normalize to YYYY-MM-DD
          const dateOf = (s: any) => String(s?.snapshot_date ?? s?.date ?? s?.day ?? '').slice(0, 10);
          const activeUsers = snaps
            .map((s: any) => ({ date: dateOf(s), users: Number(s?.active_users ?? s?.activeUsers ?? s?.users ?? 0) }))
            .filter((p) => p.date)
            .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          // module views: sum module_views.top ({module, views}) across all snapshots in the window
          const viewsByModule: Record<string, number> = {};
          for (const s of snaps) {
            const mv = s?.module_views;
            const top: any[] = Array.isArray(mv) ? mv : Array.isArray(mv?.top) ? mv.top : [];
            for (const m of top) {
              const k = String(m?.module ?? m?.name ?? '');
              if (!k) continue;
              viewsByModule[k] = (viewsByModule[k] ?? 0) + Number(m?.views ?? m?.count ?? 0);
            }
          }
          const pageViewsByModule = Object.entries(viewsByModule)
            .map(([module, views]) => ({ module, views }))
            .sort((a, b) => b.views - a.views);
          // 2026-09-24 (Jaime 14:28): role-segmented module views — live since the
          // snapshotter writes module_views.by_module_role ({module: {role: views}}).
          const viewsByModuleRole: Record<string, Record<string, number>> = {};
          for (const s of snaps) {
            const bmr = s?.module_views?.by_module_role;
            if (!bmr || typeof bmr !== 'object') continue;
            for (const [mod, roles] of Object.entries(bmr as Record<string, unknown>)) {
              if (!roles || typeof roles !== 'object') continue;
              viewsByModuleRole[mod] = viewsByModuleRole[mod] ?? {};
              for (const [role, cnt] of Object.entries(roles as Record<string, unknown>)) {
                const n = Number(cnt ?? 0);
                if (!Number.isFinite(n) || n <= 0) continue;
                viewsByModuleRole[mod][role] = (viewsByModuleRole[mod][role] ?? 0) + n;
              }
            }
          }
          // 2026-09-24 (bugfix #143): StackedHBarChart expects {label, byRole};
          // mapping module→label so module names render instead of "undefined".
          const pageViewsByModuleRole = Object.entries(viewsByModuleRole)
            .map(([module, byRole]) => ({ label: module, byRole }))
            .sort((a, b) =>
              Object.values(b.byRole).reduce((x: number, y) => x + y, 0) -
              Object.values(a.byRole).reduce((x: number, y) => x + y, 0));
          setUsageTrends({
            activeUsers,
            pageViewsByModule: pageViewsByModule.length ? pageViewsByModule : undefined,
            pageViewsByModuleRole: pageViewsByModuleRole.length ? pageViewsByModuleRole : undefined,
            source: 'platform-snapshots',
          });
          return;
        }
      } catch { /* endpoint not available yet — fall back */ }
      const res = await api.get('/analytics/trends', { params: { days } });
      const env = res && res.data !== undefined ? res.data : res;
      const d = env && env.data !== undefined ? env.data : env;
      setUsageTrends(d ?? null);
    } catch {
      setUsageTrends(null);
    } finally {
      setUsageLoading(false);
    }
  }, [superAdmin]);

  const loadAllCondos = useCallback(async () => {
    if (!superAdmin) return;
    setAllCondosLoading(true);
    try {
      const pageSize = 100;
      const first = await api.get('/condos', { params: { page: 1, pageSize } });
      const env = first && first.data !== undefined ? first.data : first;
      const d = env && env.data !== undefined ? env.data : env;
      const total = Number(d?.total ?? 0);
      let items: Condo[] = Array.isArray(d?.items) ? d.items : [];
      const pages = Math.min(3, Math.ceil(total / pageSize));
      for (let p = 2; p <= pages; p++) {
        const rp = await api.get('/condos', { params: { page: p, pageSize } });
        const envp = rp && rp.data !== undefined ? rp.data : rp;
        const dp = envp && envp.data !== undefined ? envp.data : envp;
        if (Array.isArray(dp?.items)) items = items.concat(dp.items);
      }
      setAllCondos(items);
    } catch {
      setAllCondos([]);
    } finally {
      setAllCondosLoading(false);
    }
  }, [superAdmin]);

  // 2026-09-24 (rewire): load QA test matrix + history from the super-admin
  // endpoint GET /api/admin/test-matrix (cache:'no-store' so the dashboard
  // reflects the latest tester updates without a hard reload). Graceful:
  // non-ok response, non-JSON body (e.g. HTML), or null matrix → section stays hidden.
  const loadTestMatrix = useCallback(async () => {
    if (!superAdmin) return;
    setTestMatrixLoading(true);
    try {
      // 2026-09-24 (bugfix #144): raw fetch() sent no Authorization header → 401.
      // Use the shared authenticated axios instance (Bearer token interceptor,
      // same as /admin/platform-snapshots). 401/403 or any failure → section stays hidden.
      const res = await api.get('/admin/test-matrix');
      const env = res && res.data !== undefined ? res.data : res;
      const d = env && env.data !== undefined ? env.data : env;
      setTestMatrix(d?.matrix ?? null);
      setTestMatrixHistory(d?.history ?? null);
    } catch {
      setTestMatrix(null);
      setTestMatrixHistory(null);
    } finally {
      setTestMatrixLoading(false);
    }
  }, [superAdmin]);

  // 2026-09-24: rewired to the REAL findings ledger — GET /api/admin/bug-trend?days=N
  // (BE aggregates the quejas board: findings created per day vs resolved per day).
  // Shape: { success, data: { days: [{ date, encontradas, resueltas }], totals } }.
  // Custom range: endpoint is day-count based, so fetch enough days back from today
  // to cover the chosen range start, then filter client-side to [from..to].
  const loadBugTrends = useCallback(async (opts?: { window?: string; from?: string; to?: string }) => {
    if (!superAdmin) return;
    // Remember params so the auto-refresh interval re-fetches the same slice.
    trendLastQueryRef.current = opts?.from
      ? { from: opts.from, to: opts.to }
      : (opts?.window ? { window: opts.window } : undefined);
    setTrendLoading(true);
    try {
      const custom = !!(opts?.from && opts?.to);
      const DAY_MS = 86400000;
      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);
      let fetchDays: number;
      if (custom) {
        const span = Math.max(1, Math.round((Date.parse(opts!.to!) - Date.parse(opts!.from!)) / DAY_MS) + 1);
        const back = Math.max(span, Math.round((Date.parse(todayStr) - Date.parse(opts!.from!)) / DAY_MS) + 1);
        fetchDays = Math.min(365, Math.max(2, back));
      } else {
        fetchDays = Math.min(365, Math.max(2, Number(opts?.window ?? (trendWindow === 'custom' ? '30' : trendWindow)) || 30));
      }
      const res = await api.get('/admin/bug-trend', { params: { days: fetchDays } });
      const env = res && res.data !== undefined ? res.data : res;
      const d = env && env.data !== undefined ? env.data : env;
      // 2026-09-25: rows are now per-category:
      // { date, encontradas: {bug, sugerencia, queja, total}, resueltas: {...} }
      // (America/Caracas day buckets; resolvedTrackingFrom = 2026-09-24).
      const rows: TrendDay[] =
        Array.isArray(d?.days) ? d.days.filter((r: any) => r && typeof r.date === 'string') : [];
      const filtered = custom ? rows.filter(r => r.date >= opts!.from! && r.date <= opts!.to!) : rows;
      setBugTrends(filtered.length ? {
        found: filtered.map(r => ({
          date: r.date,
          bug: Number(r.encontradas?.bug) || 0,
          sugerencia: Number(r.encontradas?.sugerencia) || 0,
          queja: Number(r.encontradas?.queja) || 0,
          total: Number(r.encontradas?.total) || 0,
        })),
        resolved: filtered.map(r => ({ date: r.date, count: Number(r.resueltas?.total) || 0 })),
        resolvedTrackingFrom: typeof d?.resolvedTrackingFrom === 'string' ? d.resolvedTrackingFrom : '2026-09-24',
      } : null);
    } catch {
      // Graceful fallback: endpoint missing/unavailable -> hide series, no break.
      setBugTrends(null);
    } finally {
      setTrendLoading(false);
    }
  }, [superAdmin, trendWindow]);

  useEffect(() => {
    if (superAdmin) loadBugTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdmin]);

  // 2026-09-25 (fix batch): auto-refresh the trend every ~5 min while the page
  // stays open — silent re-fetch, no full page reload; keeps the manual window
  // toggles working exactly as before.
  useEffect(() => {
    if (!superAdmin) return;
    const id = setInterval(() => { loadBugTrends(trendLastQueryRef.current); }, 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdmin]);

  useEffect(() => {
    if (superAdmin) { loadUsageTrends(usageWindow); loadAllCondos(); loadTestMatrix(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdmin]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!superAdmin) {
        setAnalytics(null);
        setStatsLoading(false);
        return;
      }
      setStatsLoading(true);
      setStatsError(null);
      try {
        const res = await api.get('/analytics/platform');
        const data = res.data?.data ?? res.data;
        setAnalytics(data && typeof data === 'object' ? data : null);
      } catch (err: any) {
        setStatsError(err?.message || 'Error al cargar analíticas');
        setAnalytics(null);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchAnalytics();
  }, [superAdmin]);

  const fetchCondos = useCallback(async () => {
    setCondosLoading(true);
    setCondosError(null);
    try {
      const res = await api.get('/condos');
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      setCondos(items);
    } catch (err: any) {
      setCondosError(err?.message || 'Error al cargar condominios');
      setCondos([]);
    } finally {
      setCondosLoading(false);
    }
  }, []);

  useEffect(() => { fetchCondos(); }, [fetchCondos]);

  // ── F2: Edit Condo ──
  // F-12 (2026-09-25): read-only hierarchy — GET /condos/:id returns
  // parentCondo + childCondos (display only; linking lives in
  // parent-link-routes.js and is intentionally not surfaced here).
  const toggleHierarchy = async (condo: Condo) => {
    if (hierarchyCondoId === condo.id) { setHierarchyCondoId(null); return; }
    setHierarchyCondoId(condo.id);
    if (hierarchyData[condo.id]) return;
    setHierarchyLoading(true);
    try {
      const res = await api.get(`/condos/${condo.id}`);
      const payload = res?.data?.data?.condominium ?? res?.data?.condominium ?? {};
      setHierarchyData(prev => ({ ...prev, [condo.id]: {
        parent: payload.parentCondo?.linkStatus === 'linked' && payload.parentCondo?.name
          ? { name: payload.parentCondo.name, status: payload.parentCondo.status }
          : null,
        children: Array.isArray(payload.childCondos)
          ? payload.childCondos.map((ch: { id: string; name: string; status?: string | null }) => ({ id: ch.id, name: ch.name, status: ch.status }))
          : [],
      } }));
    } catch { /* display-only detail: degrade silently, row stays usable */ }
    finally { setHierarchyLoading(false); }
  };

  const openEditModal = (condo: Condo) => {
    setEditCondo(condo);
    const address = condo.address_data || {};
    setEditForm({
      name: condo.name || '',
      address: address.street || address.route || '',
      street: address.street || address.route || '',
      number: address.number || address.street_number || '',
      neighborhood: address.neighborhood || address.sublocality || '',
      locality: address.locality || '',
      postal_code: address.postal_code || address.zip || '',
      city: condo.city || address.city || address.locality || '',
      country: condo.country || address.country || '',
      unit_count: condo.unit_count || 0,
      monthly_fee: condo.monthly_fee || 0,
      plan: condo.plan === 'free' ? 'free' : 'paid',
      adminEmail: '',
    });
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editCondo) return;
    setEditLoading(true);
    setEditError(null);
    try {
      // Backend contract (reconciled by Jose): super-admin PUT /api/admin/condominiums/:id
      // accepts condo fields + admin_email (assigns/promotes that user as condo admin) in one call.
      await api.put(`/admin/condominiums/${editCondo.id}`, {
        name: editForm.name,
        address_data: { ...(editCondo.address_data || {}), street: editForm.street || editForm.address, route: editForm.street || editForm.address, number: editForm.number, neighborhood: editForm.neighborhood, locality: editForm.locality, zip: editForm.postal_code, postal_code: editForm.postal_code, city: editForm.city, country: editForm.country },
        country: editForm.country,
        unit_count: Number(editForm.unit_count),
        monthly_fee: Number(editForm.monthly_fee),
        plan: editForm.plan,
        ...(editForm.adminEmail?.trim() ? { admin_email: editForm.adminEmail.trim() } : {}),
      });
      setCondos(prev => prev.map(c => c.id === editCondo.id ? {
        ...c, name: editForm.name, country: editForm.country, city: editForm.city,
        address_data: { ...(c.address_data || {}), street: editForm.street || editForm.address, route: editForm.street || editForm.address, number: editForm.number, neighborhood: editForm.neighborhood, locality: editForm.locality, zip: editForm.postal_code, postal_code: editForm.postal_code, city: editForm.city, country: editForm.country },
        plan: editForm.plan,
      } : c));
      setEditCondo(null);
      toast('✅ Condominio actualizado');
    } catch (err: any) {
      setEditError(err?.response?.data?.error || err?.message || 'Error al actualizar');
    } finally {
      setEditLoading(false);
    }
  };

  // ── F3: Deactivate / Delete ──
  const handleDeactivate = async (condo: Condo) => {
    const newStatus = condo.status === 'inactive' ? 'active' : 'inactive';
    try {
      // #370 fix: BE exposes PATCH, not PUT.
      await api.patch(`/condos/${condo.id}`, { status: newStatus });
      setCondos(prev => prev.map(c => c.id === condo.id ? { ...c, status: newStatus } : c));
      toast(`✅ ${newStatus === 'active' ? 'Activado' : 'Desactivado'}`);
    } catch (err: any) {
      toast('❌ Error al cambiar estado', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteCondo) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await api.delete(`/condos/${deleteCondo.id}`);
      setCondos(prev => prev.filter(c => c.id !== deleteCondo.id));
      setDeleteCondo(null);
      toast('✅ Condominio eliminado');
    } catch (err: any) {
      setDeleteError(err?.response?.data?.error || err?.message || 'Error al eliminar');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── F5: Assign Admin ──
  const openAdminModal = async (condo: Condo) => {
    setAdminCondo(condo);
    setAdminSearch('');
    setAdminSearchResults([]);
    setAdminError(null);
    setCurrentAdminsLoading(true);
    try {
      const res = await api.get(`/condos/${condo.id}/members`);
      const data = res.data?.data ?? res.data;
      const members = Array.isArray(data) ? data : (data?.items ?? []);
      setCurrentAdmins(members.filter((m: CondoMember) => m.role === 'admin'));
    } catch {
      setCurrentAdmins([]);
    } finally {
      setCurrentAdminsLoading(false);
    }
  };

  // D3 fix (2026-10-01, QA): the Asignar Admin search now queries the dedicated
  // GET /api/users/search endpoint (backend honors `q` and excludes
  // deleted_at users server-side, users-routes.js /search) with a 300ms
  // debounce, instead of GET /users which returned an unfiltered default list
  // that ignored the typed query. Response shape: { success, data: { users } }.
  useEffect(() => {
    const query = adminSearch.trim();
    if (!query) { setAdminSearchResults([]); setAdminSearching(false); return; }
    setAdminSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.get('/users/search', { params: { q: adminSearch } });
        const data = res.data?.data ?? res.data;
        const users = Array.isArray(data) ? data : (data?.users ?? data?.items ?? []);
        setAdminSearchResults(users.filter((u: any) => !u.deleted_at));
      } catch {
        setAdminSearchResults([]);
      } finally {
        setAdminSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [adminSearch]);

  const assignAdmin = async (userId: string) => {
    if (!adminCondo) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      const selected = adminSearchResults.find((candidate: any) => candidate.id === userId);
      if (!selected?.email) throw new Error('El usuario seleccionado no tiene email');
      await api.patch(`/condos/${adminCondo.id}/assign-admin`, { email: selected.email });
      const res = await api.get(`/condos/${adminCondo.id}/members`);
      const data = res.data?.data ?? res.data;
      const members = Array.isArray(data) ? data : (data?.items ?? []);
      setCurrentAdmins(members.filter((m: CondoMember) => m.role === 'admin'));
      setAdminSearch('');
      setAdminSearchResults([]);
      toast('✅ Admin asignado');
    } catch (err: any) {
      setAdminError(err?.response?.data?.error || err?.message || 'Error al asignar admin');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    if (!standaloneOpen || !superAdmin) return;
    const timer = window.setTimeout(async () => {
      setStandaloneLoading(true);
      setStandaloneError(null);
      try {
        const res = await unitsAPI.getStandalone(standaloneSearch);
        const payload = res.data?.data ?? res.data;
        setStandaloneUnits(Array.isArray(payload) ? payload : (payload?.units ?? []));
      } catch (err: any) {
        setStandaloneError(err?.response?.data?.error || err?.message || 'Error al cargar unidades independientes');
        setStandaloneUnits([]);
      } finally {
        setStandaloneLoading(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [standaloneOpen, standaloneSearch, superAdmin]);

  const formatStandaloneAddress = (unit: StandaloneUnit) => {
    const address = unit.address_data || {};
    const line = [address.street, address.number].filter(Boolean).join(' ');
    const locality = [address.neighborhood, address.locality, address.city || unit.city, address.postal_code].filter(Boolean).join(', ');
    return [line, locality, unit.country].filter(Boolean).join(' · ') || 'Sin dirección';
  };

  const formatStandaloneDate = (value?: string | null) => value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(value)) : '—';

  const beginEditStandalone = (unit: StandaloneUnit) => {
    const form = { unit_type: unit.unit_type || '', floor: unit.floor == null ? '' : String(unit.floor), square_meters: unit.square_meters == null ? '' : String(unit.square_meters), parking: unit.parking || '', storage: unit.storage || '', status: unit.status || unit.occupancy_status || '', household_name: unit.household_name || unit.householdName || '' };
    setStandaloneEditing(unit); setStandaloneForm(form); setStandaloneOriginal(form);
  };

  const saveStandalone = async () => {
    if (!standaloneEditing) return;
    setStandaloneSaving(true);
    try {
      const data: Record<string, unknown> = {};
      const fields: Array<[keyof typeof standaloneForm, string, (value: string) => unknown]> = [
        ['unit_type', 'unitType', value => value || null], ['floor', 'floor', value => value || null],
        ['square_meters', 'squareMeters', value => value === '' ? null : Number(value)], ['parking', 'parking', value => value || null],
        ['storage', 'storage', value => value || null], ['status', 'status', value => value || null], ['household_name', 'householdName', value => value || null]
      ];
      fields.forEach(([key, apiKey, convert]) => { if (standaloneForm[key] !== standaloneOriginal[key]) data[apiKey] = convert(standaloneForm[key]); });
      if (Object.keys(data).length) await unitsAPI.update(standaloneEditing.id, data);
      toast('✅ Unidad independiente actualizada');
      setStandaloneEditing(null);
      const res = await unitsAPI.getStandalone(standaloneSearch);
      const payload = res.data?.data ?? res.data;
      setStandaloneUnits(Array.isArray(payload) ? payload : (payload?.units ?? []));
    } catch (err: any) { toast(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'No se pudo actualizar la unidad', 'error'); }
    finally { setStandaloneSaving(false); }
  };

  const renderStandaloneUnits = () => {
    if (!standaloneOpen) return null;
    const badge = (label: string, value?: string | number | null) => value === null || value === undefined || value === '' ? null : <span className="rounded-full bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px]">{label}: {value}</span>;
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setStandaloneOpen(false)}><div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]"><div><h3 className="text-lg font-bold text-gray-900 dark:text-white">Unidades Independientes</h3><p className="text-sm text-gray-500 dark:text-gray-400">Consulta global de unidades sin condominio</p></div><button onClick={() => setStandaloneOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"><X className="w-5 h-5" /></button></div>
      <div className="p-5 overflow-y-auto max-h-[calc(90vh-90px)]">{standaloneError && <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">{standaloneError}</div>}
        <div className="relative mb-4 max-w-xl"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><input value={standaloneSearch} onChange={e => setStandaloneSearch(e.target.value)} placeholder="Buscar por unidad o propietario..." className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white" /></div>{standaloneEditing && <div className="mb-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/60 dark:bg-purple-900/20 p-4"><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"><label className="text-xs font-medium">Tipo de unidad<select value={standaloneForm.unit_type} onChange={e => setStandaloneForm({ ...standaloneForm, unit_type: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"><option value="">Selecciona</option>{['Casa','Apartamento','Townhouse','Penthouse','Local','Oficina','Otro'].map(v => <option key={v}>{v}</option>)}</select></label>{([['floor','Piso','text'],['square_meters','Metros cuadrados','number'],['parking','Estacionamiento','text'],['storage','Depósito/Bodega','text'],['household_name','Nombre del hogar','text']] as const).map(([key,label,type]) => <label key={key} className="text-xs font-medium">{label}<input type={type} min={type === 'number' ? '0' : undefined} step={type === 'number' ? '0.01' : undefined} value={standaloneForm[key]} onChange={e => setStandaloneForm({ ...standaloneForm, [key]: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm" /></label>)}<label className="text-xs font-medium">Estado<select value={standaloneForm.status} onChange={e => setStandaloneForm({ ...standaloneForm, status: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"><option value="">Selecciona</option>{['vacant','occupied','available','unoccupied'].map(v => <option key={v}>{v}</option>)}</select></label></div><div className="flex justify-end gap-2 mt-3"><button onClick={() => setStandaloneEditing(null)} className="px-3 py-2 text-sm">Cancelar</button><button onClick={saveStandalone} disabled={standaloneSaving} className="px-3 py-2 text-sm rounded-lg bg-purple-600 text-white disabled:opacity-50">{standaloneSaving ? 'Guardando...' : 'Guardar'}</button></div></div>}
        {standaloneLoading ? <div className="flex justify-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando unidades...</div> : standaloneUnits.length === 0 ? <p className="py-10 text-center text-gray-500">No hay unidades independientes que coincidan.</p> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase text-gray-500 border-b dark:border-slate-700"><th className="p-3">Unidad</th><th className="p-3">Propietario</th><th className="p-3">Dirección</th><th className="p-3">Tipo / Piso / m² / Estac. / Depósito</th><th className="p-3">Estado</th><th className="p-3">Creada</th><th className="p-3">Acción</th></tr></thead><tbody className="divide-y dark:divide-slate-700">{standaloneUnits.map(unit => <tr key={unit.id} className="align-top"><td className="p-3 font-semibold text-gray-900 dark:text-white">{unit.unit_number || '—'}</td><td className="p-3"><span className="font-medium">{unit.owner_name || 'Sin propietario'}</span>{unit.owner_email && <span className="block text-xs text-gray-500 dark:text-gray-400">{unit.owner_email}</span>}</td><td className="p-3 min-w-[220px]">{formatStandaloneAddress(unit)}</td><td className="p-3"><div className="flex flex-wrap gap-1">{badge('Tipo', unit.unit_type)}{badge('Piso', unit.floor)}{badge('m²', unit.square_meters)}{badge('Estac.', unit.parking)}{badge('Depósito', unit.storage)}</div></td><td className="p-3">{unit.status || unit.occupancy_status || '—'}</td><td className="p-3 whitespace-nowrap">{formatStandaloneDate(unit.created_at)}</td><td className="p-3"><button onClick={() => beginEditStandalone(unit)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-purple-700 bg-purple-50 rounded-lg"><Pencil className="w-3.5 h-3.5" />Editar</button></td></tr>)}</tbody></table></div>}
      </div></div></div>;
  };

  const openUnitsModal = async (condo: Condo) => {
    setUnitsCondo(condo);
    setManagedUnits([]);
    setUnitsCap(null);
    setUnitsError(null);
    setUnitSearch(''); setUnitSort({ key: 'unit_number', direction: 'asc' }); setUnitFormOpen(false); setUnitEditing(null);
    setUnitsLoading(true);
    try {
      const res = await unitsAPI.getForCondo(condo.id);
      const payload = res.data?.data ?? res.data;
      setManagedUnits(Array.isArray(payload) ? payload : (payload?.units ?? []));
      setUnitsCap(payload?.cap ?? condo.unit_count ?? null);
    } catch (err: any) {
      setUnitsError(err?.response?.data?.error || err?.message || 'Error al cargar unidades');
    } finally {
      setUnitsLoading(false);
    }
  };

  const removeUnit = async (unit: ManagedUnit) => {
    if (!unitsCondo || unit.owner || unit.resident) return;
    setUnitDeleting(unit.id);
    setUnitsError(null);
    try {
      await unitsAPI.remove(unit.id);
      setManagedUnits(prev => prev.filter(item => item.id !== unit.id));
      setCondos(prev => prev.map(c => c.id === unitsCondo.id ? { ...c, registered_units: Math.max(0, (c.registered_units ?? managedUnits.length) - 1) } : c));
      toast('✅ Unidad eliminada');
    } catch (err: any) {
      setUnitsError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'No se puede eliminar la unidad');
    } finally {
      setUnitDeleting(null);
    }
  };

  const setUnitSortBy = (key: string) => setUnitSort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  const beginAddUnit = () => { setUnitEditing(null); setUnitForm(emptyUnitForm); setUnitOriginalRich({}); setUnitFormOpen(true); };
  const beginEditUnit = (unit: ManagedUnit) => { setUnitEditing(unit.id); const rich = { unit_type: unit.unit_type || '', floor: unit.floor || '', square_meters: unit.square_meters == null ? '' : String(unit.square_meters), parking: unit.parking || '', storage: unit.storage || '' }; setUnitOriginalRich(rich); setUnitForm({ ...emptyUnitForm, unit_number: unit.unit_number || '', section: unit.section_name || '', internal_address: unit.internal_address || '', status: unit.status || '', ...rich }); setUnitFormOpen(true); };
  const saveUnit = async () => {
    if (!unitsCondo || !unitForm.unit_number.trim()) { setUnitsError('El número de unidad es obligatorio.'); return; }
    setUnitSaving(true); setUnitsError(null);
    try {
      const data: Record<string, unknown> = { unitNumber: unitForm.unit_number.trim(), internal_address: unitForm.internal_address.trim() || undefined, occupancyStatus: unitForm.status.trim() || undefined, section: unitForm.section.trim() || undefined };
      const rich = { unitType: unitForm.unit_type, floor: unitForm.floor, squareMeters: unitForm.square_meters === '' ? undefined : Number(unitForm.square_meters), parking: unitForm.parking, storage: unitForm.storage }; Object.entries(rich).forEach(([key, value]) => { const text = value === undefined ? '' : String(value); const oldKey = key === 'unitType' ? 'unit_type' : key === 'squareMeters' ? 'square_meters' : key; if (!unitEditing || text !== (unitOriginalRich[oldKey] || '')) data[key] = value ?? null; });
      if (unitEditing) { const res = await unitsAPI.update(unitEditing, data); const updated = res.data?.data?.unit ?? res.data?.unit ?? res.data; setManagedUnits(prev => prev.map(item => item.id === unitEditing ? { ...item, ...updated, unit_number: updated?.unit_number ?? data.unitNumber, internal_address: updated?.internal_address ?? data.internal_address, status: updated?.status ?? data.occupancyStatus, section_name: updated?.section_name ?? data.section } : item)); toast('✅ Unidad actualizada'); }
      else { const res = await unitsAPI.createInCondo(unitsCondo.id, data); const created = res.data?.data?.unit ?? res.data?.unit ?? res.data; setManagedUnits(prev => [...prev, { ...created, unit_number: created?.unit_number ?? data.unitNumber, internal_address: created?.internal_address ?? data.internal_address, status: created?.status ?? data.occupancyStatus, section_name: created?.section_name ?? data.section }]); toast('✅ Unidad agregada'); }
      setUnitFormOpen(false); setUnitEditing(null);
    } catch (err: any) { setUnitsError(err?.response?.data?.error || err?.message || 'No se pudo guardar la unidad'); } finally { setUnitSaving(false); }
  };

  const renderUnitsModal = () => {
    if (!unitsCondo) return null;
    const person = (value?: { name?: string; email?: string } | null, empty = 'Sin asignar') => value ? <><span className="font-medium">{value.name || 'Sin nombre'}</span>{value.email && <span className="block text-xs text-gray-500 dark:text-gray-400">{value.email}</span>}</> : <span className="text-gray-400">{empty}</span>;
    const query = unitSearch.trim().toLowerCase();
    const filtered = managedUnits.filter(u => !query || [u.unit_number, u.section_name, u.owner?.name, u.owner?.email, u.resident?.name, u.resident?.email].join(' ').toLowerCase().includes(query));
    const sorted = [...filtered].sort((a, b) => { const val = (u: ManagedUnit) => unitSort.key === 'owner' ? `${u.owner?.name || ''} ${u.owner?.email || ''}` : unitSort.key === 'resident' ? `${u.resident?.name || ''} ${u.resident?.email || ''}` : String((u as any)[unitSort.key] || ''); const n = val(a).localeCompare(val(b), 'es', { numeric: true, sensitivity: 'base' }); return unitSort.direction === 'asc' ? n : -n; });
    const icon = (key: string) => unitSort.key !== key ? <ArrowUpDown className="w-3.5 h-3.5 opacity-50" /> : unitSort.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
    const header = (key: string, label: string) => <th className="p-3"><button onClick={() => setUnitSortBy(key)} className="inline-flex items-center gap-1 hover:text-purple-600">{label}{icon(key)}</button></th>;
    const atCapacity = unitsCap !== null && managedUnits.length >= unitsCap;
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setUnitsCondo(null)}><div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]"><div><h3 className="text-lg font-bold text-gray-900 dark:text-white">Gestionar Unidades</h3><p className="text-sm text-gray-500 dark:text-gray-400">{unitsCondo.name} · <span className="font-semibold">{managedUnits.length} / {unitsCap ?? '—'}</span> registradas / capacidad</p></div><button onClick={() => setUnitsCondo(null)} className="p-1 text-gray-400"><X className="w-5 h-5" /></button></div>
      <div className="p-5 overflow-y-auto max-h-[calc(90vh-90px)]">{unitsError && <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">{unitsError}</div>}
      <div className="flex flex-col sm:flex-row gap-2 mb-4"><div className="relative flex-1"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><input value={unitSearch} onChange={e => setUnitSearch(e.target.value)} placeholder="Buscar unidad, sección, propietario o residente..." className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white" /></div><button onClick={beginAddUnit} disabled={atCapacity} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-purple-600 text-white disabled:opacity-50"><Plus className="w-4 h-4" />Agregar Unidad</button></div>{atCapacity && <p className="mb-4 text-xs text-amber-700 dark:text-amber-300">Capacidad alcanzada ({unitsCap} unidades). Aumenta la capacidad para agregar otra.</p>}
      {unitFormOpen && <div className="mb-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/60 dark:bg-purple-900/20 p-4"><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">{(['unit_number','section','internal_address','status'] as const).map((key, i) => <label key={key} className="text-xs font-medium text-gray-600 dark:text-gray-300">{['Unidad *','Sección','Dirección interna','Estado'][i]}<input value={unitForm[key]} onChange={e => setUnitForm({ ...unitForm, [key]: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm" /></label>)}<label className="text-xs font-medium">Tipo de unidad<select value={unitForm.unit_type} onChange={e => setUnitForm({ ...unitForm, unit_type: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"><option value="">Selecciona</option>{['Casa','Apartamento','Townhouse','Penthouse','Local','Oficina','Otro'].map(v => <option key={v}>{v}</option>)}</select></label>{([['floor','Piso','text'],['square_meters','Metros cuadrados','number'],['parking','Estacionamiento','text'],['storage','Depósito/Bodega','text']] as const).map(([key,label,type]) => <label key={key} className="text-xs font-medium">{label}<input type={type} min={type === 'number' ? '0' : undefined} step={type === 'number' ? '0.01' : undefined} value={unitForm[key]} onChange={e => setUnitForm({ ...unitForm, [key]: e.target.value })} className="mt-1 w-full px-2 py-2 rounded-lg border dark:border-slate-600 bg-white dark:bg-slate-700 text-sm" /></label>)}</div><div className="flex justify-end gap-2 mt-3"><button onClick={() => { setUnitFormOpen(false); setUnitEditing(null); }} className="px-3 py-2 text-sm">Cancelar</button><button onClick={saveUnit} disabled={unitSaving} className="px-3 py-2 text-sm rounded-lg bg-purple-600 text-white disabled:opacity-50">{unitSaving ? 'Guardando...' : 'Guardar'}</button></div></div>}
      {unitsLoading ? <div className="flex justify-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando unidades...</div> : sorted.length === 0 ? <p className="py-10 text-center text-gray-500">{managedUnits.length ? 'No hay unidades que coincidan.' : 'No hay unidades registradas.'}</p> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase text-gray-500 border-b dark:border-slate-700">{header('unit_number','Unidad')}{header('section_name','Sección')}{header('internal_address','Dirección interna')}{header('status','Estado')}{header('owner','Propietario')}{header('resident','Residente')}<th className="p-3">Acción</th></tr></thead><tbody className="divide-y dark:divide-slate-700">{sorted.map(unit => { const removable = !unit.owner && !unit.resident; return <tr key={unit.id} className="align-top"><td className="p-3 font-semibold text-gray-900 dark:text-white">{unit.unit_number}</td><td className="p-3">{unit.section_name || '—'}</td><td className="p-3">{unit.internal_address || '—'}<div className="mt-1 flex flex-wrap gap-1">{([[unit.unit_type, 'Tipo'], [unit.floor, 'Piso'], [unit.square_meters == null ? '' : `${unit.square_meters} m²`, 'm²'], [unit.parking, 'Estac.'], [unit.storage, 'Depósito']] as const).filter(([v]) => v != null && String(v).trim()).map(([v,l]) => <span key={l} className="rounded-full bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px]">{l}: {v}</span>)}</div></td><td className="p-3">{unit.status || unit.occupancy_status || '—'}</td><td className="p-3">{person(unit.owner, 'Sin propietario')}</td><td className="p-3">{person(unit.resident, 'Sin residente')}</td><td className="p-3 whitespace-nowrap"><button onClick={() => beginEditUnit(unit)} className="inline-flex items-center gap-1 px-2 py-1 mr-2 text-xs text-purple-700 bg-purple-50 rounded-lg"><Pencil className="w-3.5 h-3.5" />Editar</button>{removable ? <button onClick={() => removeUnit(unit)} disabled={unitDeleting === unit.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" />{unitDeleting === unit.id ? 'Eliminando...' : 'Eliminar'}</button> : <span className="text-xs text-gray-400">No disponible</span>}</td></tr>; })}</tbody></table></div>}</div></div></div>;
  };

  const removeAdmin = async (userId: string) => {
    if (!adminCondo) return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      // #371 fix: BE exposes PATCH, not PUT.
      await api.patch(`/condos/${adminCondo.id}/members/${userId}`, { role: 'resident' });
      setCurrentAdmins(prev => prev.filter(a => a.user_id !== userId));
      toast('✅ Admin removido');
    } catch (err: any) {
      setAdminError(err?.response?.data?.error || err?.message || 'Error al remover admin');
    } finally {
      setAdminLoading(false);
    }
  };

  // Dashboards tile: browser-tab navigation cannot send Authorization headers,
  // so first fetch a short-lived signed link via the authenticated axios
  // instance, then open it in a new tab (2026-09-11).
  const openDashboards = async () => {
    setDashboardsLoading(true);
    setDashboardsError(null);
    try {
      const res = await api.get('/admin/dashboards/audit-dashboard/link');
      const url: string | undefined = res.data?.data?.url;
      if (!url) throw new Error('No se pudo generar el enlace del dashboard');
      window.location.assign(url); // same tab (Jaime 2026-09-11)
    } catch (err: any) {
      setDashboardsError(err?.response?.data?.error || err?.message || 'No se pudo abrir el dashboard');
    } finally {
      setDashboardsLoading(false);
    }
  };

  // Management tiles
  // accent: icon badge gradient + hover border accent (cyclic palette of 6)
  const tiles: Array<{ label: string; icon: React.ComponentType<{ className?: string }>; to?: string; href?: string; onClick?: () => void; desc: string; superOnly: boolean; accent: string }> = [
    { label: 'Condominios', icon: Building2, onClick: () => { setCondosOpen(true); }, desc: 'Editar, unidades y admins', superOnly: false, accent: 'from-purple-500 to-indigo-600 hover:border-purple-300 dark:hover:border-purple-500/60' },
    { label: 'Unidades Independientes', icon: Building2, onClick: () => { setStandaloneOpen(true); setStandaloneSearch(''); }, desc: 'Consulta global sin condominio', superOnly: true, accent: 'from-fuchsia-500 to-purple-600 hover:border-fuchsia-300 dark:hover:border-fuchsia-500/60' },
    { label: 'Gestionar Usuarios', icon: Users, to: '/user-management', desc: 'Roles y accesos', superOnly: false, accent: 'from-blue-500 to-blue-700 hover:border-blue-300 dark:hover:border-blue-500/60' },
    { label: 'Crear Condominio', icon: Plus, to: '/create-condo', desc: 'Alta de condominio', superOnly: false, accent: 'from-green-500 to-emerald-700 hover:border-green-300 dark:hover:border-green-500/60' },
    { label: 'Configurar Módulos', icon: Settings, to: '/module-config', desc: 'Activar / desactivar', superOnly: false, accent: 'from-amber-500 to-orange-600 hover:border-amber-300 dark:hover:border-amber-500/60' },
    { label: 'Analíticas', icon: TrendingUp, to: '/superadmin/analytics', desc: 'Reportes y métricas', superOnly: true, accent: 'from-rose-500 to-red-600 hover:border-rose-300 dark:hover:border-rose-500/60' },
    { label: 'Uso del Producto', icon: BarChart3, to: '/superadmin/usage', desc: 'Uso visual por módulo', superOnly: true, accent: 'from-cyan-500 to-blue-600 hover:border-cyan-300 dark:hover:border-cyan-500/60' },
    { label: 'Solicitudes de Condominio', icon: Building2, to: '/superadmin/condo-requests', desc: 'Aprobar nuevos condominios', superOnly: true, accent: 'from-teal-500 to-cyan-600 hover:border-teal-300 dark:hover:border-teal-500/60' },
    { label: 'Cierre Mensual', icon: ClipboardCheck, to: '/monthly-closure', desc: 'Contabilidad', superOnly: false, accent: 'from-indigo-500 to-violet-600 hover:border-indigo-300 dark:hover:border-indigo-500/60' },
    { label: 'Directiva', icon: Briefcase, to: '/directive', desc: 'Panel de la directiva', superOnly: false, accent: 'from-blue-500 to-blue-700 hover:border-blue-300 dark:hover:border-blue-500/60' },
    { label: 'Alta Masiva', icon: Upload, to: '/admin/bulk-onboarding', desc: 'Cargar propietarios desde CSV', superOnly: false, accent: 'from-emerald-500 to-teal-700 hover:border-emerald-300 dark:hover:border-emerald-500/60' },
    { label: 'Configuración de Precios', icon: DollarSign, to: '/admin/pricing', desc: 'Planes y tarifas', superOnly: true, accent: 'from-green-500 to-emerald-700 hover:border-green-300 dark:hover:border-green-500/60' },
    { label: 'Matriz de Permisos en Vivo', icon: ShieldCheck, to: '/admin/entitlements-matrix', desc: 'Entitlements efectivos por usuario', superOnly: true, accent: 'from-amber-500 to-orange-600 hover:border-amber-300 dark:hover:border-amber-500/60' },
    { label: 'Dashboards', icon: LayoutDashboard, onClick: () => { void openDashboards(); }, desc: 'Status y auditoría interna', superOnly: true, accent: 'from-rose-500 to-red-600 hover:border-rose-300 dark:hover:border-rose-500/60' },
    { label: 'Pagos (Openpay)', icon: CreditCard, to: '/openpay-config', desc: 'Pasarela de pago', superOnly: true, accent: 'from-teal-500 to-cyan-600 hover:border-teal-300 dark:hover:border-teal-500/60' },
  ].filter(t => !t.superOnly || superAdmin);

  const roleLabel = superAdmin ? 'Super Admin' : 'Admin';
  const roleSubLabel = superAdmin ? '👑 Control Total' : 'Panel de Administración';
  const roleGreeting = superAdmin ? '👑 Bienvenido, Super Admin' : 'Bienvenido, Admin';

  const fmtCurrency = (val: number | null | undefined, currency: string = 'USD') => {
    if (val === null || val === undefined || isNaN(val)) return '—';
    try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency === 'MXN' ? 'MXN' : 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val); }
    catch { return `$${val.toLocaleString()}`; }
  };

  const fmtNum = (val: number | null | undefined) => {
    if (val === null || val === undefined || isNaN(val)) return '—';
    return val.toLocaleString();
  };

  const churnRate = analytics && analytics.total_users > 0
    ? ((analytics.inactive_users / analytics.total_users) * 100).toFixed(1) : null;

  const statsDisplay = [
    { label: 'Condominios', value: analytics ? fmtNum(analytics.total_condos) : '—', emoji: '🏢', sub: analytics ? `${analytics.active_condos} activos / ${analytics.inactive_condos} inactivos` : undefined },
    { label: 'MRR', value: analytics ? fmtCurrency(analytics.monthly_revenue, analytics.currency) : '—', emoji: '💰', sub: 'Ingresos mensuales recurrentes' },
    { label: 'Usuarios Activos', value: analytics ? fmtNum(analytics.active_users) : '—', emoji: '👥', sub: analytics ? `${fmtNum(analytics.total_users)} total` : undefined },
    { label: 'Churn %', value: churnRate !== null ? `${churnRate}%` : '—', emoji: '📉', sub: churnRate !== null ? `${analytics!.inactive_users} inactivos` : undefined },
  ];

  const hasData = analytics !== null;

  // 2026-09-24 (Jaime 14:12): richer charts — hover tooltip with exact values,
  // more x ticks (all for 7d, ~6 evenly spaced for 30d, rotated when dense),
  // integer-only y gridlines (~5-6 ticks), dual y-axes for dual-series charts.
  // Still inline SVG, no chart lib dep, var(--v-*) tokens (dark/light safe).

  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

  // Integer-friendly y ticks (~count ticks, always includes 0), never fractional
  const yTicks = (max: number, count = 5): number[] => {
    const c = Math.max(2, Math.min(6, count));
    const raw = Math.max(1, max) / (c - 1);
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / mag;
    const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
    let top = step * (c - 1);
    if (top < Math.max(1, max)) top += step;
    const ticks: number[] = [];
    for (let v = 0; v <= top + 1e-9; v += step) ticks.push(Math.round(v));
    return ticks;
  };

  // x tick indexes: all when sparse (7d), ~6-7 evenly spaced + last when dense (30d)
  const xTickIndexes = (n: number): number[] => {
    if (n <= 10) return Array.from({ length: n }, (_, i) => i);
    const every = Math.ceil(n / 6);
    const out: number[] = [];
    for (let i = 0; i < n; i += every) out.push(i);
    if (out[out.length - 1] !== n - 1) out.push(n - 1);
    return out;
  };

  // Hover tooltip pinned above the hovered column; theme-aware
  const ChartTooltip = ({ leftPct, lines, color }: { leftPct: number; lines: string[]; color?: string }) => (
    <div
      className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg px-2.5 py-1.5 text-xs shadow-lg border bg-white text-gray-900 border-gray-200 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600"
      style={{ left: `${Math.min(90, Math.max(10, leftPct))}%`, top: 0 }}
    >
      {color && <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ backgroundColor: color }} />}
      {lines.map((l, i) => (
        <div key={i} className={i === 0 ? 'text-[10px] font-semibold text-gray-500 dark:text-gray-400' : 'font-bold'}>{l}</div>
      ))}
    </div>
  );

  // Dual-series combo chart: encontradas/day bars (LEFT axis) + resueltas/day
  // line (RIGHT axis), labeled axes + legend, gridlines, column hover tooltip.
  // 2026-09-25 (fix batch): grouped-by-category combo chart — three encontradas
  // bars per day (bug / sugerencia / queja, distinct colors) + resueltas total
  // line (right axis). Legend + hover tooltip per category. America/Caracas
  // day buckets come from the backend.
  const BugTrendChart = ({ data }: { data: any }) => {
    const found: { date: string; bug: number; sugerencia: number; queja: number; total: number }[] = data?.found ?? [];
    const resolved: { date: string; count: number }[] = data?.resolved ?? [];
    const [hover, setHover] = useState<number | null>(null);
    if (!found.length) return null;
    const W = 720, H = 215, padL = 48, padR = 48, padT = 12, padB = 42;
    const n = found.length;
    const step = (W - padL - padR) / n;
    const tl = yTicks(Math.max(1, ...found.map(d => Math.max(d.bug, d.sugerencia, d.queja, d.total))), 5);
    const tr = yTicks(Math.max(1, ...resolved.map(d => d.count)), 5);
    const maxL = tl[tl.length - 1], maxR = tr[tr.length - 1];
    const x = (i: number) => padL + (i + 0.5) * step;
    const yL = (v: number) => padT + (H - padT - padB) * (1 - v / maxL);
    const yR = (v: number) => padT + (H - padT - padB) * (1 - v / maxR);
    // 3 grouped bars per day with a small intra-group gap.
    const CATS = [
      { key: 'bug' as const, label: 'Bugs', color: '#ef4444' },
      { key: 'sugerencia' as const, label: 'Sugerencias', color: '#f59e0b' },
      { key: 'queja' as const, label: 'Quejas', color: '#3b82f6' },
    ];
    const groupW = Math.min(30, Math.max(3, step * 0.7));
    const bw = Math.max(1.5, groupW / 3 - 1);
    const fmt = (d: string) => d?.slice(5);
    const rot = n > 10;
    const xTicks = xTickIndexes(n);
    const midY = padT + (H - padT - padB) / 2;
    return (
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-1 text-xs font-bold text-gray-500 dark:text-gray-400">
          {CATS.map(c => (
            <span key={c.key} className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded inline-block" style={{ backgroundColor: c.color }} /> {c.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded bg-emerald-500 inline-block" /> Resueltas (día)</span>
          <span className="ml-auto">Máx/día: {Math.max(maxL, maxR)}</span>
        </div>
        <div className="relative">
          {hover !== null && found[hover] && (
            <ChartTooltip
              leftPct={(x(hover) / W) * 100}
              color="#ef4444"
              lines={[
                String(found[hover]?.date ?? ''),
                `Bugs: ${found[hover]?.bug ?? 0}`,
                `Sugerencias: ${found[hover]?.sugerencia ?? 0}`,
                `Quejas: ${found[hover]?.queja ?? 0}`,
                `Resueltas: ${resolved[hover]?.count ?? 0}`,
              ]}
            />
          )}
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Tendencia de hallazgos por día y categoría"
            onMouseLeave={() => setHover(null)}>
            {/* gridlines + y ticks: left = encontradas, right = resueltas (integer) */}
            {tl.map((v, i) => (
              <g key={`gl${i}`}>
                <line x1={padL} y1={yL(v)} x2={W - padR} y2={yL(v)} stroke="var(--v-border-light)" strokeDasharray="3 3" opacity={v === 0 ? 1 : 0.6} />
                <text x={padL - 5} y={yL(v) + 3} fontSize="10" textAnchor="end" fill="#ef4444">{v.toLocaleString()}</text>
              </g>
            ))}
            {tr.map((v, i) => (
              <text key={`gr${i}`} x={W - padR + 5} y={yR(v) + 3} fontSize="10" fill="#10b981">{v.toLocaleString()}</text>
            ))}
            {/* full-column hover bands + row highlight */}
            {found.map((d, i) => (
              <rect key={`hit${d.date}`} x={padL + i * step} y={padT} width={step} height={H - padT - padB}
                fill={hover === i ? 'var(--v-border-light)' : 'transparent'} opacity={0.25}
                onMouseEnter={() => setHover(i)} />
            ))}
            {/* axis lines */}
            <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="var(--v-border-light)" />
            <line x1={W - padR} y1={padT} x2={W - padR} y2={H - padB} stroke="var(--v-border-light)" />
            {/* grouped bars: encontradas por categoría (left axis) */}
            {found.map((d, i) => CATS.map((c, ci) => (
              <rect key={`${d.date}-${c.key}`} x={x(i) - groupW / 2 + ci * (groupW / 3)} y={yL(d[c.key])}
                width={bw} height={Math.max(0, H - padB - yL(d[c.key]))} rx={1} fill={c.color}
                opacity={hover === null || hover === i ? 0.9 : 0.45} />
            )))}
            {/* line: resueltas total (right axis) */}
            {n > 1 && (
              <polyline points={resolved.map((d, i) => `${x(i)},${yR(d.count)}`).join(' ')} fill="none" stroke="#10b981" strokeWidth={2} opacity={hover === null ? 1 : 0.5} />
            )}
            {resolved.map((d, i) => (
              <circle key={d.date} cx={x(i)} cy={yR(d.count)} r={hover === i ? 4 : 2.5} fill="#10b981" />
            ))}
            {/* x ticks (more dates; rotated when dense) */}
            {xTicks.map(i => (
              <text key={`x${i}`} x={x(i)} y={H - 22} fontSize="10" textAnchor={rot ? 'end' : 'middle'} fill="var(--v-text-tertiary)"
                transform={rot ? `rotate(-35 ${x(i)} ${H - 22})` : undefined}>{fmt(found[i]?.date)}</text>
            ))}
            {/* axis labels */}
            <text x={(padL + W - padR) / 2} y={H - 2} fontSize="10" textAnchor="middle" fill="var(--v-text-tertiary)">Fecha</text>
            <text x={12} y={midY} fontSize="10" textAnchor="middle" fill="#ef4444" transform={`rotate(-90 12 ${midY})`}>Encontradas</text>
            <text x={W - 4} y={midY} fontSize="10" textAnchor="middle" fill="#10b981" transform={`rotate(90 ${W - 4} ${midY})`}>Resueltas</text>
          </svg>
        </div>
      </div>
    );
  };

  // Single-series line chart: data: {date, value}[]; gridlines, tooltip, labels
  const LineTrendChart = ({ data, color = '#8b5cf6', ariaLabel, valueLabel = 'Usuarios' }: { data: { date: string; value: number }[]; color?: string; ariaLabel: string; valueLabel?: string }) => {
    const [hover, setHover] = useState<number | null>(null);
    if (!data.length) return null;
    const W = 720, H = 215, padL = 48, padR = 14, padT = 12, padB = 42;
    const n = data.length;
    const ticks = yTicks(Math.max(1, ...data.map(d => d.value)), 6);
    const max = ticks[ticks.length - 1];
    const x = (i: number) => padL + (n === 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
    const y = (v: number) => padT + (H - padT - padB) * (1 - v / max);
    const fmt = (d: string) => d?.slice(5);
    const rot = n > 10;
    const xTicks = xTickIndexes(n);
    const bandW = n > 1 ? (W - padL - padR) / (n - 1) : 40;
    const midY = padT + (H - padT - padB) / 2;
    return (
      <div>
        <div className="flex items-center gap-4 mb-1 text-xs font-bold text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded inline-block" style={{ backgroundColor: color }} /> {valueLabel}</span>
          <span className="ml-auto">Máx/día: {Math.max(1, ...data.map(d => d.value))}</span>
        </div>
        <div className="relative">
          {hover !== null && data[hover] && (
            <ChartTooltip
              leftPct={(x(hover) / W) * 100}
              color={color}
              lines={[String(data[hover]?.date ?? ''), `${valueLabel}: ${data[hover]?.value ?? 0}`]}
            />
          )}
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={ariaLabel}
            onMouseLeave={() => setHover(null)}>
            {/* gridlines + integer y ticks */}
            {ticks.map((v, i) => (
              <g key={`g${i}`}>
                <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="var(--v-border-light)" strokeDasharray="3 3" opacity={v === 0 ? 1 : 0.6} />
                <text x={padL - 5} y={y(v) + 3} fontSize="10" textAnchor="end" fill="var(--v-text-tertiary)">{v.toLocaleString()}</text>
              </g>
            ))}
            {/* hover bands */}
            {data.map((d, i) => (
              <rect key={`hit${d.date}`} x={x(i) - bandW / 2} y={padT} width={bandW} height={H - padT - padB}
                fill={hover === i ? 'var(--v-border-light)' : 'transparent'} opacity={0.25}
                onMouseEnter={() => setHover(i)} />
            ))}
            <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="var(--v-border-light)" />
            {n > 1 && <polyline points={data.map((d, i) => `${x(i)},${y(d.value)}`).join(' ')} fill="none" stroke={color} strokeWidth={2} />}
            {data.map((d, i) => (
              <circle key={d.date} cx={x(i)} cy={y(d.value)} r={hover === i ? 4.5 : 2.5} fill={color} />
            ))}
            {xTicks.map(i => (
              <text key={`x${i}`} x={x(i)} y={H - 22} fontSize="10" textAnchor={rot ? 'end' : 'middle'} fill="var(--v-text-tertiary)"
                transform={rot ? `rotate(-35 ${x(i)} ${H - 22})` : undefined}>{fmt(data[i]?.date)}</text>
            ))}
            {/* axis labels */}
            <text x={(padL + W - padR) / 2} y={H - 2} fontSize="10" textAnchor="middle" fill="var(--v-text-tertiary)">Fecha</text>
            <text x={12} y={midY} fontSize="10" textAnchor="middle" fill="var(--v-text-tertiary)" transform={`rotate(-90 12 ${midY})`}>{valueLabel}</text>
          </svg>
        </div>
      </div>
    );
  };

  // Horizontal bar chart: data: {label, value}[] (already sorted desc by caller).
  // Exact values always visible in the value column; hover adds % of total.
  const HBarChart = ({ data, color = '#3b82f6', ariaLabel }: { data: { label: string; value: number }[]; color?: string; ariaLabel: string }) => {
    if (!data.length) return null;
    const max = Math.max(1, ...data.map(d => d.value));
    const total = data.reduce((a, d) => a + d.value, 0);
    return (
      <div className="space-y-2" role="img" aria-label={ariaLabel}>
        {data.map((d, i) => (
          <div key={d.label} className="flex items-center gap-2 text-sm">
            <span className="w-32 flex-shrink-0 truncate font-bold text-gray-700 dark:text-gray-300" title={d.label}>{d.label}</span>
            <div className="flex-1 h-4 rounded bg-gray-100 dark:bg-slate-700/60 overflow-hidden">
              <div className="h-full rounded" style={{ width: `${(d.value / max) * 100}%`, backgroundColor: color, opacity: 0.85 }} title={`${d.label}: ${d.value.toLocaleString()} (${total ? ((d.value / total) * 100).toFixed(1) : '0'}%)`} />
            </div>
            <span className="w-12 text-right text-xs font-bold text-gray-500 dark:text-gray-400" title={`${d.label}: ${d.value.toLocaleString()} (${total ? ((d.value / total) * 100).toFixed(1) : '0'}%)`}>{d.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  };

  // 2026-09-24 (Jaime 14:28): Uso del producto dividido por tipo de usuario.
  const ROLE_COLORS: Record<string, string> = {
    propietario: '#3b82f6', residente: '#10b981', resident: '#10b981', admin: '#8b5cf6',
    super_admin: '#ef4444', security: '#f59e0b', empleado: '#06b6d4', familiar: '#ec4899',
    directivo: '#84cc16', anonimo: '#94a3b8',
  };
  const ROLE_LABELS: Record<string, string> = {
    propietario: 'Propietario', residente: 'Residente', resident: 'Residente', admin: 'Admin',
    super_admin: 'Super Admin', security: 'Seguridad', empleado: 'Empleado', familiar: 'Familiar',
    directivo: 'Directivo', anonimo: 'Anónimo',
  };
  const roleAxisLabel = (r: string) => ROLE_LABELS[r] ?? (r ? r.charAt(0).toUpperCase() + r.slice(1) : '—');
  const roleSegColor = (r: string, i = 0) => ROLE_COLORS[r] ?? CHART_COLORS[i % CHART_COLORS.length];

  // Stacked horizontal bars segmented by role: data: {label, byRole}[]
  // (sorted desc by total from the loader). Per-segment hover tooltip with the
  // exact count + % of module; legend totals per role. Falls back to plain
  // HBarChart when by_module_role is absent (graceful degradation).
  const StackedHBarChart = ({ data, ariaLabel }: { data: { label: string; byRole: Record<string, number> }[]; ariaLabel: string }) => {
    const [hover, setHover] = useState<{ mod: string; role: string } | null>(null);
    if (!data.length) return null;
    const totals = data.map(d => Object.values(d.byRole).reduce((a: number, b) => a + b, 0));
    const max = Math.max(1, ...totals);
    const roleTotals: Record<string, number> = {};
    for (const d of data) for (const [r, v] of Object.entries(d.byRole)) roleTotals[r] = (roleTotals[r] ?? 0) + v;
    const roles = Object.entries(roleTotals).sort((a, b) => b[1] - a[1]);
    const hovered = hover ? data.find(d => d.label === hover.mod) : null;
    return (
      <div>
        <div className="relative">
          {hover && hovered && (
            <ChartTooltip
              leftPct={50}
              color={roleSegColor(hover.role)}
              lines={[hover.mod, `${roleAxisLabel(hover.role)}: ${(hovered.byRole[hover.role] ?? 0).toLocaleString()} vistas`]}
            />
          )}
          <div className="space-y-2" role="img" aria-label={ariaLabel} onMouseLeave={() => setHover(null)}>
            {data.map((d, di) => {
              const total = totals[di];
              return (
                <div key={d.label} className="flex items-center gap-2 text-sm">
                  <span className="w-32 flex-shrink-0 truncate font-bold text-gray-700 dark:text-gray-300" title={d.label}>{d.label}</span>
                  <div className="flex-1 h-4 rounded bg-gray-100 dark:bg-slate-700/60 overflow-hidden flex">
                    {Object.entries(d.byRole).sort((a, b) => b[1] - a[1]).map(([role, v], ri) => (
                      <div key={role} className="h-full"
                        style={{ width: `${(v / max) * 100}%`, backgroundColor: roleSegColor(role, ri), opacity: hover && !(hover.mod === d.label && hover.role === role) ? 0.5 : 0.9 }}
                        onMouseEnter={() => setHover({ mod: d.label, role })}
                        title={`${d.label} — ${roleAxisLabel(role)}: ${v.toLocaleString()} (${total ? ((v / total) * 100).toFixed(1) : '0'}%)`}
                      />
                    ))}
                  </div>
                  <span className="w-12 text-right text-xs font-bold text-gray-500 dark:text-gray-400">{total.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs font-bold text-gray-500 dark:text-gray-400">
          {roles.map(([r, v], ri) => (
            <span key={r} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: roleSegColor(r, ri) }} />
              {roleAxisLabel(r)} <span className="font-normal">{v.toLocaleString()}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  // Donut chart: data: {label, value}[]; colors cycle CHART_COLORS.
  // Legend shows exact count + percentage (Jaime 14:12 requirement met).
  const DonutChart = ({ data, ariaLabel, totalLabel, colorMap }: { data: { label: string; value: number }[]; ariaLabel: string; totalLabel: string; colorMap?: Record<string, string> }) => {
    const items = data.filter(d => d.value > 0);
    if (!items.length) return null;
    const total = items.reduce((a, d) => a + d.value, 0);
    const R = 60, C = 2 * Math.PI * R;
    let offset = 0;
    return (
      <div className="flex items-center gap-6 flex-wrap">
        <svg viewBox="0 0 160 160" className="w-40 h-40 flex-shrink-0" role="img" aria-label={ariaLabel}>
          {items.map((d, i) => {
            const frac = d.value / total;
            const dash = frac * C;
            const el = (
              <circle key={d.label} cx="80" cy="80" r={R} fill="none"
                stroke={colorMap?.[d.label] ?? CHART_COLORS[i % CHART_COLORS.length]} strokeWidth="22"
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset}
                transform="rotate(-90 80 80)">
                <title>{`${d.label}: ${d.value.toLocaleString()} (${((frac) * 100).toFixed(1)}%)`}</title>
              </circle>
            );
            offset += dash;
            return el;
          })}
          <text x="80" y="84" textAnchor="middle" fontSize="20" fontWeight="bold" fill="var(--v-text-primary)">{total.toLocaleString()}</text>
          <text x="80" y="100" textAnchor="middle" fontSize="9" fill="var(--v-text-tertiary)">{totalLabel}</text>
        </svg>
        <div className="space-y-1 text-sm">
          {items.map((d, i) => (
            <div key={d.label} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded inline-block flex-shrink-0" style={{ backgroundColor: colorMap?.[d.label] ?? CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="font-bold text-gray-700 dark:text-gray-300">{d.label}</span>
              <span className="text-gray-500 dark:text-gray-400">{d.value.toLocaleString()} ({((d.value / total) * 100).toFixed(0)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 2026-09-24 (Jaime 14:20): QA matrix charts — suite-state donut (estado counts,
  // paused suites shown separately) + progress-over-time line from history
  // transitions (running aprobadas count). Same tooltip/gridline style as above.
  const QA_STATUS_COLORS: Record<string, string> = {
    aprobada: '#22c55e',
    en_curso: '#eab308',
    pendiente: '#94a3b8',
    fallida: '#ef4444',
    bloqueada: '#f87171',
  };
  const QA_STATUS_LABELS: Record<string, string> = {
    aprobada: 'Aprobadas', en_curso: 'En curso', pendiente: 'Pendientes', fallida: 'Fallidas', bloqueada: 'Bloqueadas',
  };

  const TestMatrixSection = () => {
    const rows: any[] = Array.isArray(testMatrix?.rows) ? testMatrix.rows : [];
    const entries: any[] = Array.isArray(testMatrixHistory?.entries) ? testMatrixHistory.entries : [];
    const [hoverDonut, setHoverDonut] = useState<string | null>(null);
    if (!rows.length) return null;

    // ── suite-state counts (exclude paused suites from the active counts) ──
    const counts: Record<string, number> = { aprobada: 0, en_curso: 0, pendiente: 0, fallida: 0, bloqueada: 0 };
    let paused = 0;
    for (const r of rows) {
      const st = String(r.status ?? 'pendiente');
      if (r.paused) { paused += 1; continue; }
      counts[st] = (counts[st] ?? 0) + 1;
    }
    const donutData = Object.entries(counts).filter(([, v]) => v > 0).map(([label, value]) => ({ label, value }));
    const activeTotal = donutData.reduce((a, d) => a + d.value, 0);

    // ── progress over time: running count of aprobadas from history transitions ──
    const approvedTs: number[] = [];
    for (const e of entries) {
      if (e?.to === 'aprobada' && e?.ts) {
        const t = Date.parse(String(e.ts));
        if (!isNaN(t)) approvedTs.push(t);
      }
    }
    approvedTs.sort((a, b) => a - b);
    const dayKeys: string[] = [];
    const dayCounts: number[] = [];
    for (const t of approvedTs) {
      const d = new Date(t);
      const k = `${String(d.getUTCFullYear()).padStart(4, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      if (dayKeys.length && dayKeys[dayKeys.length - 1] === k) dayCounts[dayCounts.length - 1] += 1;
      else { dayKeys.push(k); dayCounts.push(1); }
    }
    let run = 0;
    const progress = dayKeys.map((k, i) => { run += dayCounts[i]; return { date: k.slice(5), value: run }; });
    if (progress.length === 1) progress.unshift({ date: 'inicio', value: 0 });

    const colorOf = (label: string) => QA_STATUS_COLORS[label] ?? '#8b5cf6';
    return (
      <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-teal-500" />
            <h3 className="font-bold text-gray-900 dark:text-white">Matriz de pruebas</h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {activeTotal} suites{paused > 0 ? ` · ${paused} pausadas` : ''}
            </span>
          </div>
          <a href="/audit-dashboard.html" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold rounded-lg bg-teal-600 text-white hover:bg-teal-700">
            <ExternalLink className="w-4 h-4" /> Ver dashboard completo
          </a>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* suite-state donut */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Estado de suites</h4>
            <div className="relative">
              {hoverDonut && (
                <ChartTooltip
                  leftPct={50}
                  color={colorOf(hoverDonut)}
                  lines={[QA_STATUS_LABELS[hoverDonut] ?? hoverDonut, `${counts[hoverDonut] ?? 0} suites (${activeTotal ? (((counts[hoverDonut] ?? 0) / activeTotal) * 100).toFixed(1) : '0'}%)`]}
                />
              )}
              <div className="flex items-center gap-6 flex-wrap" onMouseLeave={() => setHoverDonut(null)}>
                <svg viewBox="0 0 160 160" className="w-40 h-40 flex-shrink-0" role="img" aria-label="Estado de suites de la matriz de pruebas">
                  {(() => {
                    const R = 60, C = 2 * Math.PI * R;
                    let offset = 0;
                    return donutData.map((d) => {
                      const frac = d.value / activeTotal;
                      const dash = frac * C;
                      const el = (
                        <circle key={d.label} cx="80" cy="80" r={R} fill="none"
                          stroke={colorOf(d.label)} strokeWidth="22"
                          strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset}
                          transform="rotate(-90 80 80)"
                          onMouseEnter={() => setHoverDonut(d.label)}>
                          <title>{`${QA_STATUS_LABELS[d.label] ?? d.label}: ${d.value} (${(frac * 100).toFixed(1)}%)`}</title>
                        </circle>
                      );
                      offset += dash;
                      return el;
                    });
                  })()}
                  <text x="80" y="84" textAnchor="middle" fontSize="20" fontWeight="bold" fill="var(--v-text-primary)">{activeTotal}</text>
                  <text x="80" y="100" textAnchor="middle" fontSize="9" fill="var(--v-text-tertiary)">suites</text>
                </svg>
                <div className="space-y-1 text-sm">
                  {donutData.map((d) => (
                    <div key={d.label} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded inline-block flex-shrink-0" style={{ backgroundColor: colorOf(d.label) }} />
                      <span className="font-bold text-gray-700 dark:text-gray-300">{QA_STATUS_LABELS[d.label] ?? d.label}</span>
                      <span className="text-gray-500 dark:text-gray-400">{d.value.toLocaleString()} ({((d.value / activeTotal) * 100).toFixed(0)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* progress-over-day line from history */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Progreso (pruebas aprobadas por día)</h4>
            {progress.length >= 2 ? (
              <LineTrendChart
                data={progress}
                color="#14b8a6"
                ariaLabel="Progreso de pruebas aprobadas por día"
                valueLabel="Aprobadas"
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                Historial insuficiente para la tendencia ({approvedTs.length} aprobaciones registradas).
              </p>
            )}
          </div>
        </div>
        {/* 2026-09-24 (Jaime 15:36): per-row detail — findings chips + linked bugs */}
        {(() => {
          const BUG_STATUS_COLORS: Record<string, string> = {
            abierta: '#ef4444', en_proceso: '#f59e0b', resuelta: '#22c55e', cerrada: '#22c55e',
          };
          const BUG_STATUS_LABELS: Record<string, string> = {
            abierta: 'Abierta', en_proceso: 'En proceso', resuelta: 'Resuelta', cerrada: 'Cerrada',
          };
          const bugTime = (iso: any) => {
            const t = Date.parse(String(iso ?? ''));
            if (isNaN(t)) return null;
            const d = new Date(t);
            return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
          };
          return (
            <div className="mt-6">
              <button type="button" onClick={toggleMatrixDetail}
                className="w-full flex items-center gap-2 text-left group"
                aria-expanded={matrixDetailOpen}>
                <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform ${matrixDetailOpen ? '' : '-rotate-90'}`} />
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">Detalle por fila</h4>
              </button>
              <div className={matrixDetailOpen ? 'space-y-2 mt-2' : 'hidden'}>
                {rows.map((r) => {
                  const findings: string[] = Array.isArray(r.findings) ? r.findings.map((f: any) => String(typeof f === 'object' && f !== null ? (f.label ?? f.id ?? '') : f)).filter(Boolean) : [];
                  const bugs: any[] = Array.isArray(r.bugs) ? r.bugs : [];
                  const bugsOpen = bugs.filter((b) => !['resuelta', 'cerrada'].includes(String(b?.status ?? ''))).length;
                  const bugsResolved = bugs.length - bugsOpen;
                  const st = String(r.status ?? 'pendiente');
                  return (
                    <div key={String(r.id)} className="rounded-xl border border-gray-100 dark:border-[var(--v-surface-3)] p-4 bg-[var(--v-surface-2)]">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: colorOf(st) }}>
                          {QA_STATUS_LABELS[st] ?? st}
                        </span>
                        <span className="font-bold text-sm text-gray-900 dark:text-white">{String(r.title ?? r.id)}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {findings.length} hallazgos · {bugsOpen} bugs abiertos · {bugsResolved} resueltos
                        </span>
                      </div>
                      {r.area ? <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{String(r.area)}</div> : null}
                      {(findings.length > 0 || bugs.length > 0) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {findings.map((f, i) => (
                            <span key={`f-${i}`} title={`Hallazgo: ${f}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                              {f}
                            </span>
                          ))}
                          {bugs.map((b) => {
                            const bSt = String(b?.status ?? 'abierta');
                            const c = BUG_STATUS_COLORS[bSt] ?? '#8b5cf6';
                            const resolvedAt = bugTime(b?.resolved_at);
                            return (
                              <a key={String(b?.id)} href={`/quejas?ticket=${encodeURIComponent(String(b?.id ?? ''))}`}
                                title={String(b?.title ?? '')}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: c }}>
                                <Bug className="w-3 h-3" />
                                <span>#{String(b?.id ?? '')}</span>
                                <span>{BUG_STATUS_LABELS[bSt] ?? bSt}</span>
                                {resolvedAt && ['resuelta', 'cerrada'].includes(bSt) ? <span className="font-medium opacity-90">arreglado {resolvedAt}</span> : null}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ── Modals ──
  const renderEditModal = () => {
    if (!editCondo) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditCondo(null)}>
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
            <div className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Editar Condominio</h3>
            </div>
            <button onClick={() => setEditCondo(null)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5 space-y-4">
            {editError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{editError}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nombre *</label>
              <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
            </div>
            <fieldset className="rounded-xl border border-gray-200 dark:border-slate-600 p-4">
              <legend className="px-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Dirección completa</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {([['street','Calle / vía'],['number','Número'],['neighborhood','Colonia / barrio'],['locality','Localidad'],['postal_code','Código postal'],['city','Ciudad'],['country','País']] as const).map(([key, label]) => (
                  <label key={key} className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}
                    <input type="text" value={editForm[key] || ''} onChange={e => setEditForm({ ...editForm, [key]: e.target.value, ...(key === 'street' ? { address: e.target.value } : {}) })}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Capacidad máxima de unidades</label>
                <input type="number" value={editForm.unit_count} onChange={e => setEditForm({ ...editForm, unit_count: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cuota Mensual</label>
                <input type="number" value={editForm.monthly_fee} onChange={e => setEditForm({ ...editForm, monthly_fee: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Asignar administrador</label>
              <input type="email" value={editForm.adminEmail || ''} onChange={e => setEditForm({ ...editForm, adminEmail: e.target.value })}
                placeholder="admin@ejemplo.com" className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
              <p className="mt-1 text-xs text-gray-500">Se vinculará este usuario como administrador del condominio.</p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/60 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Información del condominio</p>
              <p className="text-sm text-gray-600 dark:text-gray-400"><span className="font-medium">Dirección:</span> {editCondo.address_formatted || 'Sin dirección registrada'}</p>
              <div className="text-sm text-gray-600 dark:text-gray-400"><span className="font-medium">Administradores:</span>{' '}
                {editCondo.admins?.length ? editCondo.admins.map((admin) => <span key={admin.user_id} className="block ml-2">{admin.name || 'Sin nombre'} · {admin.email}</span>) : 'Sin asignar'}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400"><span className="font-medium">Unidades registradas / capacidad:</span> {editCondo.registered_units ?? 0} / {editCondo.unit_count ?? '—'}</p>
              <div className="text-sm text-gray-600 dark:text-gray-400"><span className="font-medium">Usuarios por rol:</span>{' '}
                {Object.entries(editCondo.users_by_role || {}).length ? Object.entries(editCondo.users_by_role || {}).map(([role, count]) => <span key={role} className="mr-2 inline-block">{role}: {count}</span>) : 'Sin datos'}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Plan</label>
              <select value={editForm.plan} onChange={e => setEditForm({ ...editForm, plan: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500">
                <option value="free">Gratis</option>
                <option value="paid">Pago</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 dark:border-[var(--v-surface-3)]">
            <button onClick={() => setEditCondo(null)} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
            <button onClick={handleEditSave} disabled={editLoading} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
              {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!deleteCondo) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDeleteCondo(null)}>
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Eliminar Condominio</h3>
            </div>
            <button onClick={() => setDeleteCondo(null)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5">
            {deleteError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center gap-2 mb-4">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{deleteError}</p>
              </div>
            )}
            <p className="text-gray-700 dark:text-gray-300 mb-2">¿Estás seguro? Esta acción no se puede deshacer.</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Se eliminará el condominio <strong className="text-gray-900 dark:text-white">{deleteCondo.name}</strong> y todos sus datos asociados.</p>
          </div>
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 dark:border-[var(--v-surface-3)]">
            <button onClick={() => setDeleteCondo(null)} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">Cancelar</button>
            <button onClick={handleDelete} disabled={deleteLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Eliminar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAdminModal = () => {
    if (!adminCondo) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAdminCondo(null)}>
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
            <div className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Asignar Admin</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">— {adminCondo.name}</span>
            </div>
            <button onClick={() => setAdminCondo(null)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-5 space-y-4">
            {adminError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{adminError}</p>
              </div>
            )}
            {/* Current admins */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Administrador(es) actual(es)</h4>
              {currentAdminsLoading ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
              ) : currentAdmins.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Sin admin asignado</p>
              ) : (
                <div className="space-y-2">
                  {currentAdmins.map(admin => (
                    <div key={admin.user_id} className="flex items-center justify-between bg-gray-50 dark:bg-slate-700 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                          <Shield className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{admin.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{admin.email}</p>
                        </div>
                      </div>
                      <button onClick={() => removeAdmin(admin.user_id)} disabled={adminLoading}
                        className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30">Remover</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Search */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Buscar usuario</h4>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={adminSearch} onChange={e => setAdminSearch(e.target.value)} placeholder="Buscar por email o nombre..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500" />
                {adminSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
              </div>
              {adminSearch.trim() && !adminSearching && adminSearchResults.length === 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 px-1">Sin resultados para “{adminSearch.trim()}”.</p>
              )}
              {adminSearchResults.length > 0 && (
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                  {adminSearchResults.map((u: any) => (
                    <button key={u.id} onClick={() => assignAdmin(u.id)} disabled={adminLoading}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 dark:hover:bg-slate-700 text-left transition">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-slate-600 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.name || u.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                      </div>
                      <span className="text-xs text-purple-600 font-medium">Asignar →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 2026-09-24 (Jaime 13:00): the Condominios list card moved behind a platform-
  // administration tile. Same markup as before, wrapped in the same full-screen
  // modal shell used by "Unidades Independientes" — nothing removed, data and
  // actions (crear/editar/unidades/admin/desactivar/eliminar) all stay reachable.
  const renderCondosModal = () => {
    if (!condosOpen) return null;
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setCondosOpen(false)}><div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[var(--v-surface-3)]">
        <div><h3 className="text-lg font-bold text-gray-900 dark:text-white">Condominios</h3><p className="text-sm text-gray-500 dark:text-gray-400">Gestiona todos los condominios de la plataforma</p></div>
        <div className="flex items-center gap-2"><button onClick={() => navigate('/create-condo')} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"><Plus className="w-4 h-4" /> Crear</button><button onClick={() => setCondosOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white"><X className="w-5 h-5" /></button></div>
      </div>
      <div className="p-5 overflow-y-auto max-h-[calc(90vh-90px)]">
        {condosLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando condominios...
          </div>
        ) : condosError ? (
          <div className="p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700 dark:text-red-300 font-medium mb-1">Error al cargar condominios</p>
            <p className="text-red-500 text-sm mb-3">{condosError}</p>
            <button onClick={fetchCondos} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Reintentar</button>
          </div>
        ) : condos.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">No hay condominios</p>
            <p className="text-gray-500 text-sm mb-4">Crea el primer condominio para comenzar.</p>
            <button onClick={() => navigate('/create-condo')} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Crear Condominio
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {condos.map(condo => {
              const isActive = condo.status !== 'inactive';
              return (
                <div key={condo.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 dark:text-white truncate">{condo.name || 'Sin nombre'}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-400'}`}>
                          {isActive ? '● Activo' : '○ Inactivo'}
                        </span>
                        {condo.plan && (() => {
                          const isPaidPlan = condo.plan !== 'free';
                          const monthlyFee = Number(condo.monthly_fee);
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isPaidPlan ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                              {isPaidPlan && monthlyFee > 0 ? `paid · ${fmtCurrency(monthlyFee, condo.primary_currency)} ${condo.primary_currency || 'USD'}` : isPaidPlan ? 'paid' : 'free'}
                            </span>
                          );
                        })()}
                        {condo.parentCondo?.linkStatus === 'linked' && condo.parentCondo?.name && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" title="Condominio vinculado a un condominio padre (solo lectura)">
                            ↳ parte de {condo.parentCondo.name}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        <span className="basis-full text-gray-600 dark:text-gray-300">📍 {condo.address_formatted || condo.address_data?.street || condo.address_data?.city || 'Sin dirección'}</span>
                        {condo.country && <span>🌐 {condo.country}</span>}
                        <span>🏠 Unidades registradas / capacidad: {condo.registered_units ?? 0} / {condo.unit_count ?? '—'}</span>
                        <span>👤 Administrador: {condo.admins?.length ? condo.admins.map(a => `${a.name || 'Sin nombre'} (${a.email})`).join(', ') : 'Sin asignar'}</span>
                        {condo.monthly_fee != null && condo.monthly_fee > 0 && <span>💰 {fmtCurrency(condo.monthly_fee, condo.primary_currency)}</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Usuarios por rol">
                        {Object.entries(condo.users_by_role || {}).map(([role, count]) => <span key={role} className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">{role}: {count}</span>)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button onClick={() => openEditModal(condo)} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition">
                      <Pencil className="w-3.5 h-3.5" /> Configuración
                    </button>
                    <button onClick={() => openUnitsModal(condo)} className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 rounded-lg transition">
                      <Building2 className="w-3.5 h-3.5" /> Gestionar Unidades
                    </button>
                    <button onClick={() => openAdminModal(condo)} className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition">
                      <UserCog className="w-3.5 h-3.5" /> Asignar Admin
                    </button>
                    <button onClick={() => handleDeactivate(condo)} className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition ${isActive ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100' : 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100'}`}>
                      <Power className="w-3.5 h-3.5" /> {isActive ? 'Desactivar' : 'Activar'}
                    </button>
                    <button onClick={() => setDeleteCondo(condo)} className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg transition">
                      <Trash2 className="w-3.5 h-3.5" /> Eliminar
                    </button>
                  </div>
                  {hierarchyCondoId === condo.id && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-slate-700/40 border border-gray-100 dark:border-slate-600 rounded-lg p-3">
                      {(() => {
                      const hier = hierarchyData[condo.id];
                      return hierarchyLoading && !hier ? (
                        <span className="text-gray-400">Cargando jerarquía…</span>
                      ) : (
                        <>
                          <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">Jerarquía (solo lectura)</div>
                          {hier?.parent ? (
                            <div>↳ Condominio padre: <b>{hier.parent.name}</b>{hier.parent.status ? ` · ${hier.parent.status}` : ''}</div>
                          ) : (
                            <div className="text-gray-400">Condominio independiente (sin padre vinculado).</div>
                          )}
                          {hier?.children.length ? (
                            <div className="mt-1">Condominios hijos: {hier.children.map(ch => `${ch.name}${ch.status ? ` (${ch.status})` : ''}`).join(' · ')}</div>
                          ) : hier ? (
                            <div className="text-gray-400">Sin condominios hijos.</div>
                          ) : null}
                        </>
                      );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div></div></div>;
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'var(--v-font-sans)' }}>
      {renderEditModal()}
      {renderDeleteModal()}
      {renderAdminModal()}
      {renderUnitsModal()}
      {renderCondosModal()}
      {renderStandaloneUnits()}

      <header className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-b border-purple-100/50 dark:border-[var(--v-surface-3)] z-40">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-gray-600 dark:text-gray-300 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-[var(--v-surface-1)] rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">{roleLabel}</span>
                <span className="text-xs text-purple-500 block -mt-1">{roleSubLabel}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { fetchCondos(); window.location.reload(); }} className="p-2 text-gray-600 dark:text-gray-300 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-[var(--v-surface-1)] rounded-lg">
              <RefreshCw className="w-5 h-5" />
            </button>
            <div className="hidden md:block text-right">
              <p className="text-sm font-bold text-gray-900 dark:text-white">👋 {user?.name || 'Admin'}</p>
              <p className="text-xs text-purple-500">🌐 {roleLabel}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8">
        {/* Hero */}
        <div className="relative bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-600 rounded-3xl p-6 lg:p-8 text-white mb-8 shadow-2xl shadow-purple-500/25 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="relative">
            <p className="text-purple-200 text-sm font-medium mb-1">{roleGreeting}</p>
            <h1 className="text-2xl lg:text-3xl font-bold">Panel de Control 🎯</h1>
            <p className="text-purple-100 mt-2">Gestiona usuarios, condominios y configura la plataforma</p>
          </div>
        </div>

        {/* Analytics Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {!superAdmin ? null : statsLoading ? (
            <>
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-5 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
                  <div className="text-3xl mb-2">⏳</div>
                  <div className="h-8 w-20 bg-gray-100 dark:bg-slate-700 rounded animate-pulse mb-2" />
                  <div className="h-4 w-16 bg-gray-50 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              ))}
            </>
          ) : statsError ? (
            <div className="col-span-2 lg:col-span-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-2xl p-6 text-center">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 dark:text-red-300 font-medium mb-1">Error al cargar analíticas</p>
              <p className="text-red-500 text-sm">{statsError}</p>
              <button onClick={() => window.location.reload()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Reintentar</button>
            </div>
          ) : !hasData ? (
            <div className="col-span-2 lg:col-span-4 bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-8 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg text-center">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">Sin datos suficientes</p>
              <p className="text-gray-500 text-sm">Aún no hay datos analíticos para mostrar.</p>
            </div>
          ) : (
            statsDisplay.map((stat, i) => (
              <div key={i} className="group bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-5 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg hover:shadow-xl hover:border-purple-200 transition-all hover:-translate-y-1">
                <div className="text-3xl mb-2">{stat.emoji}</div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                {stat.sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.sub}</p>}
              </div>
            ))
          )}
        </div>

        {/* Analytics: Top Condos + Growth */}
        {hasData && analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-purple-500" />
                <h3 className="font-bold text-gray-900 dark:text-white">Top 5 Condominios por Usuarios</h3>
              </div>
              {analytics.top_condos_by_users && analytics.top_condos_by_users.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_condos_by_users.slice(0, 5).map((condo, i) => {
                    const maxUsers = Math.max(...analytics.top_condos_by_users.map(c => c.user_count));
                    const pct = maxUsers > 0 ? (condo.user_count / maxUsers) * 100 : 0;
                    return (
                      <div key={condo.condo_id || i} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-400 w-5">#{i + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{condo.condo_name || 'Sin nombre'}</span>
                            <span className="text-sm font-bold text-purple-600">{condo.user_count}</span>
                          </div>
                          <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">Sin datos de condominios</p>
              )}
            </div>

            {/* 30-Day Growth */}
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <h3 className="font-bold text-gray-900 dark:text-white">Crecimiento (30 días)</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-600">{fmtNum(analytics.growth_30d?.new_users)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Nuevos usuarios</p>
                </div>
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                  <Building2 className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-purple-600">{fmtNum(analytics.growth_30d?.new_condos)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Nuevos condos</p>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                  <Activity className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-600">
                    {analytics.growth_30d?.growth_percentage != null ? `${analytics.growth_30d.growth_percentage.toFixed(1)}%` : '—'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Crecimiento</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2026-09-24 (Jaime 13:00): Condominios list moved behind a platform-administration
            tile (renderCondosModal). Freed space now hosts the super-admin bugs trend
            chart (GET /api/admin/bug-trend?days=N — real findings ledger, 7/30/custom). */}
        {superAdmin && (
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg mb-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                <h3 className="font-bold text-gray-900 dark:text-white">Tendencia de bugs</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">Encontradas por categoría (bugs / sugerencias / quejas) vs. resueltas por día (registro real de hallazgos, horario Caracas)</span>
              </div>
              <div className="flex bg-gray-50 dark:bg-slate-700/60 rounded-xl p-1 border border-gray-100 dark:border-[var(--v-surface-3)]">
                {([['7','7d'],['30','30d']] as const).map(([k, label]) => (
                  <button key={k} onClick={() => { setTrendWindow(k as any); loadBugTrends({ window: k }); }}
                    className={`px-3 py-1 text-sm font-bold rounded-lg ${trendWindow === k ? 'bg-purple-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
                    {label}
                  </button>
                ))}
                <button onClick={() => setTrendWindow(w => (w === 'custom' ? '30' : 'custom'))}
                  className={`px-3 py-1 text-sm font-bold rounded-lg ${trendWindow === 'custom' ? 'bg-purple-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
                  Rango
                </button>
              </div>
            </div>
            {trendWindow === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <input type="date" value={trendFrom} onChange={e => setTrendFrom(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm" />
                <span className="text-gray-500 dark:text-gray-400">–</span>
                <input type="date" value={trendTo} onChange={e => setTrendTo(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm" />
                <button onClick={() => trendFrom && trendTo && loadBugTrends({ from: trendFrom, to: trendTo })}
                  disabled={!trendFrom || !trendTo || trendLoading}
                  className="px-3 py-1.5 bg-purple-600 text-white font-bold rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  Aplicar
                </button>
              </div>
            )}
            {trendLoading ? (
              <div className="py-8 flex justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : bugTrends?.found ? (
              // resueltas=0 before 2026-09-24 is real data from the board — no caveat needed.
              <BugTrendChart data={bugTrends} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Sin datos de tendencia (serie oculta).</p>
            )}
            {/* 2026-09-25 (fix batch): same data-availability caveat as /quejas —
                resueltas are only complete from 2026-09-24 (live stamping + 09-25 backfill). */}
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Datos disponibles desde el <strong>24 de septiembre de 2026</strong> (seguimiento de resolución); los días usan la zona horaria de Caracas (UTC-4). Se actualiza automáticamente cada 5 minutos.
            </p>
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Resumen de la comunidad en <button onClick={() => navigate('/quejas')} className="text-purple-600 dark:text-purple-400 font-bold hover:underline">Quejas y Sugerencias</button>.
            </p>
          </div>
        )}

        {/* 2026-09-24 (Jaime 14:00): comprehensive chart set. Data sources (all existing
            super_admin endpoints — nothing invented):
            - Usuarios activos por día: /analytics/trends (product analytics, activeUsers daily)
            - Uso por módulo: /analytics/trends (pageViewsByModule)
            - Usuarios por rol: /condos (users_by_role aggregated across condos)
            - Condos por país / plan: /condos (country / plan fields) */}
        {superAdmin && (
          <>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Analíticas de plataforma</h2>
            {/* Usage section shares one 7d/30d switcher (endpoint supports only 7|30) */}
            <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg mb-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-500" />
                  <h3 className="font-bold text-gray-900 dark:text-white">Uso del producto</h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500">Usuarios activos por día y vistas por módulo</span>
                </div>
                <div className="flex bg-gray-50 dark:bg-slate-700/60 rounded-xl p-1 border border-gray-100 dark:border-[var(--v-surface-3)]">
                  {([['7', '7d'], ['30', '30d']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => { setUsageWindow(k as any); loadUsageTrends(k as any); }}
                      className={`px-3 py-1 text-sm font-bold rounded-lg ${usageWindow === k ? 'bg-purple-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {usageLoading ? (
                <div className="py-8 flex justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Usuarios activos por día</h4>
                    {usageTrends?.activeUsers?.length ? (
                      <LineTrendChart
                        data={usageTrends.activeUsers.map((d: any) => ({ date: d.date, value: Number(d.users ?? d.active_users ?? 0) }))}
                        color="#8b5cf6"
                        ariaLabel="Usuarios activos por día"
                      />
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">Sin datos de actividad.</p>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                      Vistas por módulo (top 8){usageTrends?.pageViewsByModuleRole?.length ? ' · por tipo de usuario' : ''}
                    </h4>
                    {usageTrends?.pageViewsByModuleRole?.length ? (
                      <StackedHBarChart
                        data={usageTrends.pageViewsByModuleRole.slice(0, 8)}
                        ariaLabel="Vistas por módulo y tipo de usuario"
                      />
                    ) : usageTrends?.pageViewsByModule?.length ? (
                      <HBarChart
                        data={usageTrends.pageViewsByModule.slice(0, 8).map((m: any) => ({ label: m.module, value: Number(m.views ?? 0) }))}
                        color="#06b6d4"
                        ariaLabel="Vistas por módulo"
                      />
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">Sin datos de vistas.</p>
                    )}
                  </div>
                </div>
              )}
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                Detalle por usuario en <button onClick={() => navigate('/superadmin/usage')} className="text-purple-600 dark:text-purple-400 font-bold hover:underline">Uso del Producto</button>.
              </p>
            </div>

            {/* Distribution charts from /condos aggregates */}
            {allCondosLoading ? (
              <div className="py-8 flex justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : allCondos.length ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-blue-500" />
                    <h3 className="font-bold text-gray-900 dark:text-white">Usuarios por rol</h3>
                  </div>
                  <DonutChart
                    data={Object.entries(allCondos.reduce<Record<string, number>>((acc, c) => {
                      for (const [role, count] of Object.entries(c.users_by_role || {})) acc[role] = (acc[role] || 0) + Number(count || 0);
                      return acc;
                    }, {})).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value)}
                    ariaLabel="Distribución de usuarios por rol"
                    totalLabel="membresías"
                  />
                </div>
                <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-purple-500" />
                    <h3 className="font-bold text-gray-900 dark:text-white">Condos por país</h3>
                  </div>
                  <HBarChart
                    color="#8b5cf6"
                    ariaLabel="Condominios por país"
                    data={Object.entries(allCondos.reduce<Record<string, number>>((acc, c) => {
                      const k = c.country || 'Sin país';
                      acc[k] = (acc[k] || 0) + 1;
                      return acc;
                    }, {})).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8)}
                  />
                </div>
                <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-6 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    <h3 className="font-bold text-gray-900 dark:text-white">Condos por plan</h3>
                  </div>
                  <DonutChart
                    data={Object.entries(allCondos.reduce<Record<string, number>>((acc, c) => {
                      const k = c.plan || 'sin plan';
                      acc[k] = (acc[k] || 0) + 1;
                      return acc;
                    }, {})).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value)}
                    ariaLabel="Distribución de condominios por plan"
                    totalLabel="condos"
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center mb-8">Sin datos de distribución (no se pudieron cargar los condominios).</p>
            )}

            {/* 2026-09-24 (Jaime 14:20): Matriz de pruebas (QA) — /api/admin/test-matrix feed */}
            <TestMatrixSection />
          </>
        )}

        {/* Management tiles */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Administración de la plataforma</h2>
        {dashboardsError && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{dashboardsError}</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            const cardCls = `flex items-center gap-4 bg-white dark:bg-[var(--v-surface-1)] rounded-2xl p-5 border border-gray-100 dark:border-[var(--v-surface-3)] shadow-lg hover:shadow-xl ${tile.accent} transition-all hover:-translate-y-1 text-left`;
            const badgeCls = `w-12 h-12 flex-shrink-0 rounded-xl bg-gradient-to-br ${tile.accent.split(' hover:')[0]} flex items-center justify-center shadow-md`;
            const inner = (
              <>
                <div className={badgeCls}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{tile.label}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{tile.desc}</p>
                </div>
              </>
            );
            if (tile.onClick) {
              return (
                <button key={tile.label} onClick={tile.onClick} disabled={dashboardsLoading} className={`${cardCls} disabled:opacity-60 disabled:cursor-wait`}>
                  {inner}
                </button>
              );
            }
            if (tile.href) {
              return (
                <a key={tile.href} href={tile.href} target="_blank" rel="noopener noreferrer" className={cardCls}>
                  {inner}
                </a>
              );
            }
            return (
              <button key={tile.to} onClick={() => { if (tile.to) navigate(tile.to); }} className={cardCls}>
                {inner}
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}